import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Brush, CartesianGrid, Legend, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Warning } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { useFetch } from "@/lib/api";

export const LINE_COLORS = {
  G: "#64748B", F: "#818CF8", C: "#2563EB", S: "#F59E0B", I: "#06B6D4",
  "L Income": "#A7F3D0", "L 2030": "#6EE7B7", "L 2035": "#34D399", "L 2040": "#10B981", "L 2045": "#059669",
  "L 2050": "#047857", "L 2055": "#BE185D", "L 2060": "#DB2777", "L 2065": "#EC4899", "L 2070": "#A855F7", "L 2075": "#7C3AED",
};
const ORDER = Object.keys(LINE_COLORS);
const RANGES = [["3D", 3], ["1W", 5], ["2W", 10], ["1M", 21], ["3M", 63], ["6M", 126], ["1Y", 252]];
const label = (f) => (f.length === 1 ? `${f} Fund` : f);

function rebase(rows, funds, startIdx) {
  const base = rows[startIdx] || {};
  return rows.map((r) => {
    const o = { date: r.date };
    funds.forEach((f) => {
      if (r[f] != null && base[f] != null) o[f] = Math.round((((1 + r[f] / 100) / (1 + base[f] / 100)) - 1) * 100000) / 1000;
    });
    return o;
  });
}

function FlagChips({ flags }) {
  if (!flags.length) return <p className="text-xs text-muted-foreground mt-4" data-testid="recap-no-flags">No fund moved more than 1% in a single day in this range.</p>;
  return (
    <div className="mt-4" data-testid="recap-flags">
      <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1"><Warning size={13} className="text-rose-600" /> Days with a move over 1% ({flags.length})</p>
      <div className="flex flex-wrap gap-2">
        {flags.map((f) => {
          const top = f.moves[0];
          const text = <><span className="font-mono-num">{f.date.slice(5)}</span> · {top.fund.length === 1 ? `${top.fund} Fund` : top.fund} <span className={`font-mono-num ${top.change_pct > 0 ? "text-emerald-600" : "text-rose-600"}`}>{top.change_pct > 0 ? "+" : ""}{top.change_pct.toFixed(2)}%</span>{f.moves.length > 1 ? ` +${f.moves.length - 1}` : ""}</>;
          return f.recap_slug ? (
            <Link key={f.date} to={`/learn/${f.recap_slug}`} className="text-xs rounded-full border border-rose-300 dark:border-rose-500/40 px-3 py-1 hover:bg-rose-500/10 transition-colors" data-testid={`recap-flag-${f.date}`}>{text} →</Link>
          ) : (
            <span key={f.date} title="No daily recap was published for this day" className="text-xs rounded-full border border-border px-3 py-1 text-slate-600 dark:text-slate-400" data-testid={`recap-flag-${f.date}`}>{text}</span>
          );
        })}
      </div>
    </div>
  );
}

export function FundLinesChart({ end }) {
  const [sessions, setSessions] = useState(21);
  const [hidden, setHidden] = useState({});
  const [brush, setBrush] = useState(null);
  const { data, loading } = useFetch(`/prices/window?end=${end}&sessions=${sessions}`);
  const funds = useMemo(() => (data?.funds || []).slice().sort((a, b) => ORDER.indexOf(a) - ORDER.indexOf(b)), [data]);
  const rows = useMemo(() => data?.rows || [], [data]);
  const last = Math.max(0, rows.length - 1);
  const start = Math.min(brush?.startIndex ?? 0, last);
  const stop = Math.min(brush?.endIndex ?? last, last);
  const shown = useMemo(() => rebase(rows, funds, start), [rows, funds, start]);
  const pick = (s) => { setBrush(null); setSessions(s); };
  const navigate = useNavigate();
  const visibleDates = new Set(rows.slice(start, stop + 1).map((r) => r.date));
  const flags = (data?.flags || []).filter((f) => visibleDates.has(f.date));
  const flagBy = Object.fromEntries(flags.map((f) => [f.date, f]));
  const onChartClick = (e) => { const f = flagBy[e?.activeLabel]; if (f?.recap_slug) navigate(`/learn/${f.recap_slug}`); };
  return (
    <figure className="glass p-5 sm:p-6 mt-8" data-testid="recap-fund-chart">
      <figcaption className="flex flex-col md:flex-row md:items-start justify-between gap-3 mb-3">
        <div>
          <p className="font-display font-semibold text-slate-900 dark:text-white">TSP fund performance</p>
          <p className="text-xs text-muted-foreground" data-testid="recap-chart-period">
            Cumulative % change from {rows[start]?.date || "…"} to {rows[stop]?.date || end}. Drag the slider below the chart to zoom into any stretch; click a legend item to hide a fund.
          </p>
        </div>
        <div className="flex flex-wrap gap-1 shrink-0" data-testid="recap-range-buttons">
          {RANGES.map(([l, s]) => (
            <Button key={l} size="sm" variant={sessions === s ? "default" : "ghost"} className="h-7 px-2.5 text-xs" onClick={() => pick(s)} data-testid={`recap-range-${l}`}>{l}</Button>
          ))}
        </div>
      </figcaption>
      <div className="h-[420px]">
        {loading && !rows.length ? <div className="h-full grid place-items-center text-sm text-muted-foreground">Loading prices...</div> : (
          <ResponsiveContainer>
            <LineChart data={shown} margin={{ top: 8, right: 12, left: 0, bottom: 0 }} onClick={onChartClick} style={{ cursor: flags.some((f) => f.recap_slug) ? "pointer" : "default" }}>
              <CartesianGrid vertical={false} strokeOpacity={0.25} />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="#94A3B8" tickFormatter={(d) => d.slice(5)} minTickGap={24} />
              <YAxis tick={{ fontSize: 11 }} stroke="#94A3B8" width={52} domain={["auto", "auto"]} tickFormatter={(v) => `${v.toFixed(1)}%`} />
              <Tooltip formatter={(v, n) => [`${v > 0 ? "+" : ""}${Number(v).toFixed(2)}%`, n]} labelFormatter={(l) => flagBy[l] ? `Close ${l} · over 1% move${flagBy[l].recap_slug ? " (click for recap)" : ""}` : `Close ${l}`} itemSorter={(i) => -i.value} />
              {flags.map((f) => <ReferenceLine key={f.date} x={f.date} stroke="#E11D48" strokeOpacity={0.55} strokeDasharray="4 3" label={{ value: "!", position: "top", fill: "#E11D48", fontSize: 12, fontWeight: 700 }} />)}
              <Legend wrapperStyle={{ fontSize: 12, cursor: "pointer" }} itemSorter={(i) => ORDER.indexOf(i.dataKey)} onClick={(e) => setHidden((h) => ({ ...h, [e.dataKey]: !h[e.dataKey] }))} />
              {funds.map((f) => (
                <Line key={f} dataKey={f} name={label(f)} stroke={LINE_COLORS[f] || "#94A3B8"} strokeWidth={f.length === 1 ? 2.5 : 1.5} dot={rows.length <= 12} hide={!!hidden[f]} isAnimationActive={false} />
              ))}
              {rows.length > 3 && (
                <Brush key={sessions} dataKey="date" height={26} travellerWidth={8} stroke="#0891B2" tickFormatter={(d) => d.slice(5)}
                  startIndex={start} endIndex={stop} onChange={(r) => setBrush({ startIndex: r.startIndex, endIndex: r.endIndex })} />
              )}
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
      <FlagChips flags={flags} />
    </figure>
  );
}
